import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds, translateForLocations } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { flatten, deepen } from '../../versa/record.js';
import { notify } from '../../versa/msgbox.js';
import { parse } from '../../versa/misc.js';
import view from '../views/locations.js';
import form from '../forms/locations.js';
import select_printer_form from '../forms/stocks-select-printer.js';
import * as types from '../types.js';

const locations = function()
{

	let locations_crud = crud.create({
		async loadBaseData()
		{
			const q = this.queryAll();
			api.call(api.list_companies, q).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_warehouses, q).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_classes, q).then((data) => db.set('classes', data).catch(error => notify(error)));
			api.call(api.list_products, q).then((data) => db.set('products', data).catch(error => notify(error)));	// Required for location limits
			api.call(api.list_devices, q).then((data) => db.set('devices', data).catch(() => {}));
		},
		async queryData(query)
		{
			return await api.call(api.list_locations, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_location, ...JSON.parse(id));
			_record.classes = _record.classes.map(entry => [entry.class_company_id, entry.class_id]);
			_record = flatten(_record, 'limits');
			return _record;
		},
		async editFilter()
		{
			try {
				const data = await api.call(api.list_locations, '?limit=0');
				if (data && Array.isArray(data)) await db.set('locations', data);
			} catch (_) { /* ignore */ }
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) =>
				{
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					const top = 10;
					for (let i = 0; i < top; i++)
					{
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
					}
					if (parts.length === 0)
						return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) await db.set('locations', data);
				}
			});
		},
		async applyFilter(data)
		{
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.filterExplicitlyApplied = true;
			api.call(api.list_locations, '?limit=0').then((d) => { if (d && Array.isArray(d)) db.set('locations', d); }).catch(() => {});
			await this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'limits');
			let _record = {
				address: data.address,
				mix: data.mix,
				level: Number(data.level),
				in_reach: data.in_reach == '1',			// Can be: undefined, '0', '1'
				is_staging: data.is_staging == '1',		// Can be: undefined, '0', '1'
				classes: parse(data.classes).map(entry => ({ 
					class_company_id: entry[0],
					class_id: Number(entry[1])
				}))
			};
			if (data.limits)
				_record.limits = data.limits.map(limit => ({ 
					product_company_id: limit.product_company_id,
					product_id: Number(limit.product_id),
					minimum_quantity: Number(limit.minimum_quantity),
					maximum_quantity: Number(limit.maximum_quantity) 
				}))
			if (old_data)
				return await api.call(api.update_location, data.company_id, data.warehouse_id, data.id, _record);
			else
				return await api.call(api.insert_location, data.company_id, data.warehouse_id, _record);
		},
		printLocationLabels()
		{
			const sendRequest = async (data) =>
			{
				const sortVal = this.sort_column * (this.sort_order ? -1 : 1);
				const payload = {
					company_id: data.company_id,
					device_id: data.device_id,
					return_zpl: !!data.use_local_agent,
					sort: sortVal,
				};
				const filterExpr = translateForLocations(this.filter, this.fieldspecs);
				if (filterExpr)
					payload.filter = filterExpr;
				else
					payload.ids = await this.getSelectedIds(['company_id', 'warehouse_id', 'id']);
				const result = await api.call(api.print_location_labels, payload);
				if (result?.zpl)
				{
					try
					{
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					}
					catch (e)
					{
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			};
			let fs = formset.create(select_printer_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.record_count;
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém', store: 'warehouses', labels: ['name'] },
		{ index: 3, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição (armazém e endereço)', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 4, field: 'address', label: 'Endereço', types: [types.location_address(), types.caps()], placeholder: 'X-Y-Z', title: 'Endereço da posição' },
		{ index: 5, field: 'mix', label: 'Mix', types: [types.opt_location_mix(), types.datalist()], placeholder: '', title: 'Misturas permitidas na posição' },
		{ index: 6, field: 'level', label: 'Nível', types: [types.number(0, 2)], placeholder: '0', title: 'Nível da posição' },
		{ index: 7, field: 'in_reach', label: 'Atuante', types: [types.opt_bool()], placeholder: '', title: 'Posição ao alcance da mão?' },
		{ index: 8, field: 'is_staging', label: 'Staging', types: [types.opt_bool()], placeholder: '', title: 'Posição de descarga de mercadorias?' },
		{ index: 11, field: 'status', label: 'Status', types: [types.opt_location_status(), types.datalist()], placeholder: '', title: 'Status do pedido' },
	];

	const reports = [
		{ hash: '#location-labels', label: 'Etiquetas&nbsp;de&nbsp;posição' },
	];

	router.set('#location-labels', locations_crud.printLocationLabels.bind(locations_crud));

	locations_crud.run(
		'locations',
		'Posições',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		[],
		reports
	);

};

export default locations;

